import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { AUTHENTICATION_FAIL_CODE, ERROR_EXCEPTION_CODE, FORBIDDEN, NOT_FOUND_CODE } from '../../utils/constants/http';
import { MENU_ROUTES, ROUTES } from '../../utils/constants/routes';
import { HTTP_ERROR_MESSAGE } from '../../utils/constants/strings';
import { OpenToast } from '../../utils/helpers/function';

class ApiClient {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: `${process.env.REACT_APP_API_URL || 'http://localhost:1337'}/api`, // URL API
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json', // default JSON
      },
    });

    // Add interceptor to handle request
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Don't add token to header because using HttpOnly cookie
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // Add interceptor to handle response
    this.instance.interceptors.response.use(
      (response: AxiosResponse): AxiosResponse => {
        response.data = {
          ...response.data,
          message: response.data?.message || 'リクエストが成功しました',
          meta: response.data?.meta,
          details: response.data?.details || null,
          isError: false,
        };
        return response;
      },
      (error: AxiosError): Promise<AxiosResponse> => {
        this.handleResponseError(error);
        return Promise.resolve(error.response as AxiosResponse);
      }
    );
  }

  /**
   * // Response error handling function
   * @param error
   * @returns
   */
  private handleResponseError(error: AxiosError) {
    const location = window.location.pathname;
    // Check if the error is a network error
    if (error.code === 'ERR_NETWORK') {
      // Delete cookies and authentication related values
      document.cookie = 'authToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'; // Delete cookies
      // redirect to page maintenance
      if (location !== ROUTES.COMING_SOON) {
        window.location.href = ROUTES.COMING_SOON;
      }
      return;
    }

    if (error.response) {
      const { status } = error.response;

      switch (status) {
        case AUTHENTICATION_FAIL_CODE:
          // Delete cookies and authentication related values
          document.cookie = 'authToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'; // Delete cookies
          localStorage.removeItem('userInfo'); // Clear local storage

          if (
            location !== ROUTES.LOGIN &&
            location !== ROUTES.FORGOT_PASSWORD &&
            location !== ROUTES.CHECK_TOKEN_RESET_PASSWORD &&
            !location.startsWith('/reset-password/')
          ) {
            const searchParams = new URLSearchParams({
              prevURL: location || MENU_ROUTES.HOME,
              searchURL: window.location.search,
            }).toString();

            window.location.href = `${ROUTES.LOGIN}?${searchParams}`;
          }

          if (
            location !== ROUTES.LOGIN &&
            location !== ROUTES.FORGOT_PASSWORD &&
            location !== ROUTES.CHECK_TOKEN_RESET_PASSWORD &&
            !location.startsWith('/reset-password/')
          ) {
            OpenToast({ message: `${HTTP_ERROR_MESSAGE[AUTHENTICATION_FAIL_CODE]}` }, 'error');
          }
          break;
        case FORBIDDEN:
          OpenToast({ message: `${HTTP_ERROR_MESSAGE[FORBIDDEN]}` }, 'error');
          // redirect to page login
          window.location.href = ROUTES.LOGIN;
          break;
        case NOT_FOUND_CODE:
          window.location.href = '*'; // Redirect to page not found
          break;
        case ERROR_EXCEPTION_CODE:
          OpenToast({ message: `${HTTP_ERROR_MESSAGE[ERROR_EXCEPTION_CODE]}` }, 'error');
          break;
        // default:
        //   OpenToast({ message: 'エラーが発生しました。再度ご確認ください。' }, 'error');
        //   break;
      }
    } else {
      OpenToast({ message: 'サーバーが応答を受け取っていません。' }, 'error');
      return {
        status: 502,
        response: {
          status: 502,
          data: { data: null, error: { detail: null, message: 'サーバーが応答を受け取っていません。', status: 502 } },
        },
      };
    }

    return {
      status: 503,
      response: {
        status: 503,
        data: { data: null, error: { detail: null, message: 'サーバーが応答を受け取っていません。', status: 502 } },
      },
    };
  }

  // API call function
  public async request<T>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.request<T>(config);
  }

  public get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.request({ ...config, method: 'GET', url });
  }

  // GET method to download file
  public async getFile(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<Blob>> {
    return this.instance.get(url, { ...config, responseType: 'blob' });
  }

  /* eslint-disable */
  public post<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    // Check if data is FormData
    const isFormData = data instanceof FormData;

    return this.request({
      ...config,
      method: 'POST',
      url,
      data,
      headers: {
        'Content-Type': isFormData ? 'multipart/form-data' : 'application/json', // Set appropriate Content-Type
        ...config?.headers, // Combine with other headers if any
      },
    });
  }

  /* eslint-disable */
  public put<T>(url: string, data: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.request({ ...config, method: 'PUT', url, data });
  }

  public delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.request({ ...config, method: 'DELETE', url });
  }
}
const apiClient = new ApiClient();
export default apiClient;
